import he from 'he';
import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Button, Col, Row, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import { questionType, quizSettings } from '../../../../constants';
import { handleInvalidForm } from '../../../../services';
import { contentConfigSelector } from '../../../../state/modules/configuration/selectors';
import { noCapMap } from '../../../../utils';
import { ControlGroup, ControlOption, FileUploadFormField, Icon, InputFormField, NumberFormField, TextAreaFormField, ImageCropField } from '../../../components';

class ContentQuestionForm extends Component {

  constructor() {
    super();

    this.state = {
      questionData: {
        questionType: questionType.MultiChoice,
        description: '',
        questionImage: undefined,
        questionImageName: undefined,
        questionImageUrl: undefined,
        multiAnswers: [
          {
            label: 'A',
            isCorrect: false
          },
          {
            label: 'B',
            isCorrect: false
          },
          {
            label: 'C',
            isCorrect: false
          },
          {
            label: 'D',
            isCorrect: false
          },
          {
            label: 'E',
            isCorrect: false
          },
          {
            label: 'F',
            isCorrect: false
          }
        ],
        shortAnswer: {
          description: ''
        },
        numericAnswer: {
          description: ''
        }
      },
      croppedImageUrl: null,
      isSavingQuestion: false,
      showingCroppingModal: false,
      validation: {
        questionCharLimit: true,
        shortAnsCharLimit: true,
        numericAnsCharLimit: true,
        multiAnsCharLimit: true,
        question: true,
        multiChoice: true,
        shortAnswer: true,
        numericAnswer: true,
        correctAnswers: true
      }
    };
  }

  componentDidMount() {
    if (this.props.loadedQuestion && this.props.loadedQuestion.id) {
      const { loadedQuestion } = this.props;
      const preloadQuestion = {
        id: loadedQuestion.id,
        metaData: loadedQuestion.metaData,
        questionType: loadedQuestion.questionType,
        description: loadedQuestion.description,
        questionImageUrl: loadedQuestion.imageFileUrl,
        questionImageName: loadedQuestion.imageFileName,
        multiAnswers: noCapMap((a, i) => {
          if (loadedQuestion.answers[i]) {
            return Object.assign({}, a, loadedQuestion.answers[i])
          }
          else {
            return a;
          }
        })(this.state.questionData.multiAnswers),
        shortAnswer: fp.first(loadedQuestion.answers),
        numericAnswer: fp.first(loadedQuestion.answers)
      }
      this.setState({
        questionData: preloadQuestion
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.saveQuestionSuccess && this.state.isSavingQuestion) {
      this.setState({
        isSavingQuestion: false
      });
      this.toggleForm();
    }
  }

  resetValidation() {
    this.setState({
      validation: {
        questionCharLimit: true,
        shortAnsCharLimit: true,
        numericAnsCharLimit: true,
        multiAnsCharLimit: true,
        question: true,
        multiChoice: true,
        shortAnswer: true,
        numericAnswer: true,
        correctAnswers: true
      }
    });
  }

  onquestionTypeChange(event) {
    this.setState({
      questionData: Object.assign({}, this.state.questionData, { questionType: event.target.value })
    });
    this.resetValidation();
  }

  onQuestionFieldChange({ name, value }) {
    this.setState({
      questionData: Object.assign({}, this.state.questionData, { [name]: value })
    });
  }

  onShortAnswerChange({ value }) {
    const nextShortAnswer = Object.assign({}, this.state.questionData.shortAnswer, { description: value });
    this.setState({
      questionData: Object.assign({}, this.state.questionData, { shortAnswer: nextShortAnswer })
    });
  }

  onNumericAnswerChange({ value }) {
    const nextNumericAnswer = Object.assign({}, this.state.questionData.numericAnswer, { description: value });
    this.setState({
      questionData: Object.assign({}, this.state.questionData, { numericAnswer: nextNumericAnswer })
    });
  }

  onAnswerChange(event, answer) {
    const nextAnswers = fp.map((a) =>
      a.label === answer.label ? Object.assign({}, a, { description: event.target.value }) : a
    )(this.state.questionData.multiAnswers);
    this.setState({
      questionData: Object.assign({}, this.state.questionData, { multiAnswers: nextAnswers })
    });
  }

  onMarkCorrectAnswer(event, answer) {
    const nextAnswers = fp.map((a) =>
      a.label === answer.label ? Object.assign({}, a, { isCorrect: event.target.checked }) : a
    )(this.state.questionData.multiAnswers);
    this.setState({
      questionData: Object.assign({}, this.state.questionData, { multiAnswers: nextAnswers })
    });
  }

  showCroppingModal = () => {
    this.setState({
      showingCroppingModal: true
    })
  }

  hideCroppingModal = () => {
    this.setState({
      showingCroppingModal: false
    })
  }

  validateNewQuestionForm() {
    const { questionData } = this.state;
    const filledMultiChoiceAnswers = fp.filter((a) => a.description && a.description.trim() !== '')(questionData.multiAnswers);
    const correctAnswers = fp.filter((a) => a.isCorrect)(filledMultiChoiceAnswers);
    const excessiveLongAnswers = fp.filter((a) => a.description && a.description.length > quizSettings.maxLen)(questionData.multiAnswers);

    const validation = {
      questionCharLimit: questionData.description.length <= quizSettings.maxLen,
      shortAnsCharLimit: questionData.questionType !== questionType.ShortAnswer || questionData.shortAnswer.description.length <= quizSettings.maxLen,
      numericAnsCharLimit: questionData.questionType !== questionType.NumericAnswer || questionData.numericAnswer.description.length <= quizSettings.maxLen,
      multiAnsCharLimit: questionData.questionType !== questionType.MultiChoice || excessiveLongAnswers.length === 0,
      question: questionData.description.trim() !== '',
      shortAnswer: questionData.questionType !== questionType.ShortAnswer || questionData.shortAnswer.description.trim() !== '',
      numericAnswer: questionData.questionType !== questionType.NumericAnswer || questionData.numericAnswer.description.trim() !== '',
      // At least 2 anwswers are specified. 
      multiChoice: questionData.questionType !== questionType.MultiChoice || filledMultiChoiceAnswers.length > 1,
      correctAnswers: questionData.questionType !== questionType.MultiChoice || correctAnswers.length > 0
    };
    this.setState({
      validation: validation
    });
    return fp.all(fp.identity)(validation);
  }

  toggleForm() {
    this.resetValidation();
    this.props.toggleQuestionForm();
  }

  onSaveQuestion(event) {
    event.preventDefault();
    event.stopPropagation();
    const formValid = this.validateNewQuestionForm();
    this.setState({
      isSavingQuestion: formValid
    });

    if (formValid) {
      const questionData = Object.assign({}, this.state.questionData, { contentId: this.props.selectedContent.id });
      this.props.saveQuestion(questionData);
    } else if (this.formRef) {
      handleInvalidForm(this.formRef);
    }
  }

  removeImage = () => this.setState({
    questionData: Object.assign({}, this.state.questionData, {
      questionImage: undefined,
      questionImageName: undefined,
      questionImageUrl: undefined,
    }),
    croppedImageUrl: null,
    showingCroppingModal: false
  })

  onImageDrop = (acceptedFiles, rejectedFiles) => {
    const acceptedFile = fp.first(acceptedFiles);
    if (acceptedFile) {
      this.updateQuestionImage(null, acceptedFile);
      this.showCroppingModal();
    }
  }

  onImageCropped = (blob) => this.setState({
    questionData: Object.assign({}, this.state.questionData, {
      questionImage: blob
    }),
    croppedImageUrl: window.URL.createObjectURL(blob),
    showingCroppingModal: false
  })

  updateQuestionImage = (event, image) => this.setState({
    questionData: Object.assign({}, this.state.questionData, {
      questionImage: image,
    })
  })

  render() {
    const questionImage = this.state.questionData.questionImage
    const questionImageUrl = this.state.questionData.questionImageUrl
    const croppedImageUrl = this.state.croppedImageUrl
    return (
      <Row className='md-gutter'>
        <Col xs={12} md={6} mdOffset={3} className='boxed'>
          <form noValidate ref={(form) => { this.formRef = form; }} className='form--add-question' onSubmit={(e) => this.onSaveQuestion(e)}>
            <ControlGroup
              id='question-type'
              control='radio'
              type='pill'
              label={<span className='green'>Question type</span>}
              onChange={(e) => this.onquestionTypeChange(e)}>
              <ControlOption className='radio-multi' value={questionType.MultiChoice} checked={this.state.questionData.questionType === questionType.MultiChoice} type={'radio'} label={<span>Multi-choice</span>} />
              <ControlOption className='radio-numeric' value={questionType.NumericAnswer} checked={this.state.questionData.questionType === questionType.NumericAnswer} type={'radio'} label={<span>Numeric</span>} />
            </ControlGroup>

            <TextAreaFormField
              id='description'
              label='Enter question here'
              rows={2}
              validation={this.state.validation.question ? (this.state.validation.questionCharLimit ? null : `Question exceeds ${quizSettings.maxLen} characters`) : 'Question is required.'}
              value={he.decode(this.state.questionData.description)}
              onChange={(e) => this.onQuestionFieldChange({ name: e.target.name, value: he.encode(e.target.value) })} />

            {
              this.state.questionData.multiAnswers && this.state.questionData.multiAnswers.length > 0 && this.state.questionData.questionType === questionType.MultiChoice ?
                <div>
                  <Row className='sm-gutter'>
                    <Col xs={12}>
                      <label className='green'>Answers</label>
                    </Col>
                    {
                      fp.map((answer) =>
                        <Col key={answer.label} xs={12} md={6}>
                          <InputFormField
                            id='answer-a'
                            label={answer.label}
                            value={he.decode(answer.description || '')}
                            onChange={(e) => this.onAnswerChange(e, answer)} />
                        </Col>)(this.state.questionData.multiAnswers)
                    }
                    {
                      !this.state.validation.multiChoice ?
                        <Col xs={12} aria-invalid='true' className='has-error'>
                          <span id='multi-choice-help-block' className='help-block'>Please specify at least 2 answers.</span>
                        </Col> : null
                    }
                    {
                      !this.state.validation.multiAnsCharLimit ?
                        <Col xs={12} aria-invalid='true' className='has-error'>
                          <span id='multi-choice-help-block' className='help-block'>Answer exceeds {quizSettings.maxLen} Characters.</span>
                        </Col> : null
                    }
                  </Row>
                  <Row className='sm-gutter'>
                    <Col xs={12}>
                      <label className='green'>Which is the correct answer(s)?</label>
                    </Col>
                    <ControlGroup id='correct-answer' control='checkbox' label={null} colProps={{ xs: 12, sm: 3 }}>
                      {
                        fp.map(answer =>
                          <ControlOption
                            key={answer.label}
                            value={answer.label}
                            checked={answer.isCorrect}
                            onChange={(e) => this.onMarkCorrectAnswer(e, answer)}
                            label={<span>{answer.label}</span>} />
                        )(this.state.questionData.multiAnswers)
                      }
                    </ControlGroup>
                    {
                      !this.state.validation.correctAnswers ?
                        <Col xs={12} aria-invalid='true' className='has-error'>
                          <span id='multi-answer-help-block' className='help-block'>Please specify at least 1 correct answer.</span>
                        </Col> : null
                    }
                  </Row>
                </div> : null
            }
            {
              this.state.questionData.questionType === questionType.ShortAnswer ?
                <Row className='sm-gutter'>
                  <Col xs={12}>
                    <label className='green'>What is the correct answer?</label>
                  </Col>
                  <Col xs={12}>
                    <TextAreaFormField
                      id='short-answer'
                      label={'Enter correct answer here'}
                      rows={2}
                      value={he.decode(this.state.questionData.shortAnswer.description)}
                      validation={this.state.validation.shortAnswer ? (this.state.validation.shortAnsCharLimit ? null : `Answer exceeds ${quizSettings.maxLen} characters`) : 'Answer is required.'}
                      onChange={(e) => this.onShortAnswerChange({ value: he.encode(e.target.value) })} />
                  </Col>
                </Row> : null
            }
            {
              this.state.questionData.questionType === questionType.NumericAnswer ?
                <Row className='sm-gutter'>
                  <Col xs={12}>
                    <label className='green'>What is the correct answer?</label>
                  </Col>
                  <Col xs={12}>
                    <NumberFormField
                      id='numeric-answer'
                      label={'Enter correct answer here'}
                      quantityDials={false}
                      min={-1000000000}
                      max={1000000000}
                      step={0.01}
                      value={he.decode(this.state.questionData.numericAnswer.description)}
                      validation={this.state.validation.numericAnswer ? (this.state.validation.numericAnsCharLimit ? null : `Answer exceeds ${quizSettings.maxLen} characters`) : 'Answer is required.'}
                      onChange={(e) => this.onNumericAnswerChange({ value: e.text === '' ? '' : he.encode(e.value.toString()) })} />
                  </Col>
                </Row> : null
            }
            {
              questionImageUrl && !questionImage ?
                [
                  <Row key="question image title" className='sm-gutter'>
                    <Col xs={12}>
                      <label className='green'>Question Image (Optional)</label>
                    </Col>
                  </Row>,
                  <Row key="question image existing" className='sm-gutter'>
                    <Col>
                      <div className='question-image'><img alt="question" src={he.decode(questionImageUrl)} /></div>
                    </Col>
                  </Row>,
                  <Row key="question image remove" className='sm-gutter'>
                    <button aria-label='Remove Image' className='btn remove-question-image' onClick={this.removeImage}>
                      <Icon icon='close' />
                    </button>
                  </Row>
                ]
                : null
            }
            {
              croppedImageUrl ?
                [
                  <Row key="question image title" className='sm-gutter'>
                    <Col xs={12}>
                      <label className='green'>Question Image (Optional)</label>
                    </Col>
                  </Row>,
                  <Row key="question image new" className='sm-gutter'>
                    <Col>
                      <div className='question-image'><img alt="business logo" src={croppedImageUrl} /></div>
                    </Col>
                  </Row>,
                  <Row key="question image remove" className='sm-gutter'>
                    <button aria-label='Remove Image' className='btn remove-question-image' onClick={this.removeImage}>
                      <Icon icon='close' />
                    </button>
                  </Row>
                ]
                : null
            }
            {
              (!questionImage && !questionImageUrl) ? <Row className='sm-gutter'>
                <Col xs={12}>
                  <label className='green'>Question Image (Optional)</label>
                </Col>
                <FileUploadFormField
                  styleName='file-6'
                  multiple={false}
                  accept='image/png,image/jpeg'
                  onFileDrop={this.onImageDrop}
                  file={questionImage}
                  maxSize={this.props.config.maxImageFileSizeBytes}
                  dropLabel='Upload Image (JPG, PNG)' />
              </Row> : null
            }

            <Row className='sm-gutter'>
              {
                this.props.contentError ?
                  <Col xs={12} aria-invalid='true' className='has-error'>
                    <span id='content-help-block' className='help-block'>{this.props.contentError}</span>
                  </Col> : null
              }
              <Col xs={12} md={8} mdOffset={2}>
                <p>
                  <Button type='submit' className='large full'>Save question</Button>
                  <br />
                  <Button className='large full' bsStyle='plain' onClick={() => this.toggleForm()}>Cancel</Button>
                </p>
              </Col>
            </Row>
          </form>
        </Col>
        <Modal show={this.state.showingCroppingModal} onHide={this.props.hideCroppingModal} dialogClassName='image-cropping-modal'>
          <Modal.Body>
            <ImageCropField
              className='question-image'
              imageSrc={questionImage? questionImage.preview : null}
              onComplete={this.onImageCropped} />
            <button aria-label='Remove Image' className='btn remove-cropped-question-image' onClick={this.removeImage}>
              <Icon icon='close' />
            </button>
          </Modal.Body>
        </Modal>
      </Row >
    );
  }
}

const mapStateToProps = (state) => ({
  config: contentConfigSelector(state)
});

const mapDispatchToProps = (dispatch) => ({
});

ContentQuestionForm.propTypes = {
  saveQuestion: PropTypes.func.isRequired,
  toggleQuestionForm: PropTypes.func.isRequired,
  selectedContent: PropTypes.object.isRequired,
  loadedQuestion: PropTypes.object,
  contentError: PropTypes.string,
  saveQuestionSuccess: PropTypes.bool.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(ContentQuestionForm);




// WEBPACK FOOTER //
// ./src/containers/pages/shared/publish-content/content-question-form.js